import { differenceInDays, differenceInHours, differenceInMinutes, format } from 'date-fns'

/**
 * Formats a datetime string to "Month Day, Hour:Minuteam/pm" format
 * Example: "July 17th, 2:00pm"
 */
export function formatDateTime(dateTimeString: string | Date): string {
  const date = typeof dateTimeString === 'string' ? new Date(dateTimeString) : dateTimeString

  if (isNaN(date.getTime())) {
    throw new Error(`Invalid datetime: ${dateTimeString}`)
  }

  // Get the day with ordinal suffix (1st, 2nd, 3rd, etc.)
  const day = date.getDate()
  const daySuffix = getDaySuffix(day)

  // Format the month and time
  const month = format(date, 'MMMM')
  const time = format(date, 'h:mma').toLowerCase()

  return `${month} ${day}${daySuffix}, ${time}`
}

/**
 * Helper function to get the ordinal suffix for a day number
 */
function getDaySuffix(day: number): string {
  if (day >= 11 && day <= 13) {
    return 'th'
  }

  switch (day % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

export function formatMeetingDateRange(start: Date, end?: Date) {
  const now = new Date()
  const minutesToStart = differenceInMinutes(start, now)
  const hoursToStart = differenceInHours(start, now)
  const daysToStart = differenceInDays(start, now)

  // If end time is provided, check if the meeting has ended
  if (end) {
    const minutesSinceEnd = differenceInMinutes(now, end)

    // If meeting ended within the last 10 minutes
    if (minutesSinceEnd > 0 && minutesSinceEnd <= 10) {
      return 'Just ended'
    }

    // If meeting is currently happening (between start and end)
    if (now >= start && now < end) {
      return 'In progress'
    }
  }

  // If within a minute (less than 1 minute) and no end time provided
  if (minutesToStart <= 0) {
    return 'In progress'
  }
  // If within minutes (less than 1 hour)
  else if (minutesToStart > 0 && minutesToStart < 60) {
    return `In ${minutesToStart} ${minutesToStart === 1 ? 'minute' : 'minutes'}`
  }
  // If within hours (less than 24 hours)
  else if (hoursToStart > 0 && hoursToStart < 24) {
    return `In ${hoursToStart} ${hoursToStart === 1 ? 'hour' : 'hours'} at ${format(start, 'h:mma')}`
  }
  // If tomorrow
  else if (daysToStart === 1) {
    return `Tomorrow at ${format(start, 'h:mma')}`
  }
  // If further in the future
  else if (daysToStart > 1) {
    return `${format(start, 'MMM d')} at ${format(start, 'h:mma')}`
  }
  // If in the past
  else {
    return format(start, 'MMM d, h:mma')
  }
}

export function isWithinNextMinutes(date: Date, minutes: number, endDate?: Date) {
  const meetingDate = new Date(date)
  const now = new Date()
  const minutesFromNow = new Date(now.getTime() + minutes * 60 * 1000)

  // Ensure the meeting date is actually today by checking the day
  if (!isDayToday(meetingDate)) {
    return false
  }

  // If end date is provided and we're checking for "happening now" (1 minute)
  // verify that the meeting hasn't ended yet
  if (endDate && minutes <= 1) {
    const meetingEnd = new Date(endDate)
    // Meeting is happening now if we're past the start and before the end
    return meetingDate <= minutesFromNow && now < meetingEnd
  }

  if (minutes <= 1) {
    return meetingDate <= minutesFromNow
  }

  return meetingDate >= now && meetingDate <= minutesFromNow
}

/**
 * A meeting is "live" when it's underway (or starting within the next minute)
 * and hasn't ended yet, today. Intentionally delegates to the same
 * `isWithinNextMinutes(start, 1, end)` check the meeting cell uses for its
 * happening-now glow/indicator, so sort order and visual state never disagree.
 */
export function isMeetingHappeningNow(meeting: { start: Date; end?: Date }): boolean {
  return isWithinNextMinutes(meeting.start, 1, meeting.end)
}

/**
 * Sort comparator factory that floats the live meeting to the top of a list.
 * Priority:
 *   1. the meeting whose transcript is actively recording (wins outright);
 *   2. any happening-now meeting over not-yet-live ones;
 *   3. among concurrent live meetings, the one ending soonest — so a focused
 *      timed meeting surfaces above a long or all-day block, which always ends
 *      last and would otherwise dominate by virtue of its earlier start;
 *   4. otherwise chronological start order.
 * Keeps the active transcript discoverable even when an earlier-starting event
 * would otherwise sort above it.
 */
export function compareMeetingsLiveFirst(activeTranscriptEventId?: string | null) {
  type SortableMeeting = { id?: string; start: Date; end?: Date }
  return (a: SortableMeeting, b: SortableMeeting): number => {
    const aActive = !!activeTranscriptEventId && a.id === activeTranscriptEventId
    const bActive = !!activeTranscriptEventId && b.id === activeTranscriptEventId
    if (aActive !== bActive) return aActive ? -1 : 1

    const aLive = isMeetingHappeningNow(a)
    const bLive = isMeetingHappeningNow(b)
    if (aLive !== bLive) return aLive ? -1 : 1

    if (aLive && bLive) {
      const aEnd = (a.end ?? a.start).getTime()
      const bEnd = (b.end ?? b.start).getTime()
      if (aEnd !== bEnd) return aEnd - bEnd
    }

    return new Date(a.start).getTime() - new Date(b.start).getTime()
  }
}

export function isRecentlyEnded(endDate: Date) {
  const now = new Date()
  const meetingEnd = new Date(endDate)
  const minutesSinceEnd = differenceInMinutes(now, meetingEnd)

  // Meeting ended within the last 10 minutes
  return minutesSinceEnd > 0 && minutesSinceEnd <= 10
}

export function normalizeSummaryDate(summaryDate: string) {
  return new Date(`${summaryDate}T00:00:00`)
}

export function isFromMostRecentFriday(date: Date) {
  const today = new Date()

  // Find the most recent Friday (could be today if it's Friday)
  const mostRecentFriday = new Date(today)
  const diff = (today.getDay() - 5 + 7) % 7 // 5 = Friday
  mostRecentFriday.setDate(today.getDate() - diff)

  // Compare year/month/day only
  return (
    date.getFullYear() === mostRecentFriday.getFullYear() &&
    date.getMonth() === mostRecentFriday.getMonth() &&
    date.getDate() === mostRecentFriday.getDate()
  )
}

export function isDayYesterday(dateToCheck: Date) {
  // Get today's date and set its time to midnight
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // Get the date to check and set its time to midnight
  const checkedDate = new Date(dateToCheck) // Create a new Date object to avoid modifying the original
  checkedDate.setHours(0, 0, 0, 0)

  // Calculate the date for yesterday at midnight
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)

  // Compare the timestamps (milliseconds since epoch)
  return checkedDate.getTime() === yesterday.getTime()
}

export function isDayToday(dateToCheck: Date) {
  // Get today's date and set its time to midnight
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // Get the date to check and set its time to midnight
  const checkedDate = new Date(dateToCheck) // Create a new Date object to avoid modifying the original
  checkedDate.setHours(0, 0, 0, 0)

  // Compare year, month, and day explicitly to avoid timezone edge cases
  return (
    checkedDate.getFullYear() === today.getFullYear() &&
    checkedDate.getMonth() === today.getMonth() &&
    checkedDate.getDate() === today.getDate()
  )
}

export function isDayTomorrow(dateToCheck: Date) {
  // Get today's date and set its time to midnight
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // Get the date to check and set its time to midnight
  const checkedDate = new Date(dateToCheck) // Create a new Date object to avoid modifying the original
  checkedDate.setHours(0, 0, 0, 0)

  // Calculate the date for tomorrow at midnight
  const tomorrow = new Date(today)
  tomorrow.setDate(today.getDate() + 1)

  // Compare the timestamps (milliseconds since epoch)
  return checkedDate.getTime() === tomorrow.getTime()
}
